import { useCallback, useEffect, useState } from 'react';

// returns new sets on change, otherwise original
const add = (set: Set<string>, id: string) => {
  if (set.has(id)) return set;

  return new Set(set).add(id);
};
const remove = (set: Set<string>, id: string) => {
  if (!set.has(id)) return set;

  const next = new Set(set);
  next.delete(id);
  return next;
};

/**
 * Helper for tracking local state of "~bulk" add or remove actions in one opening of the modal.
 *
 * Useful as an alternative to refetching search results after every action.
 */
export const useTrackAddRemoveActions = (modalOpen: boolean) => {
  // keep local state of objects that have been added/removed so long as the modal stays open
  const [addedIds, setAddedIds] = useState(new Set<string>());
  const [removedIds, setRemovedIds] = useState(new Set<string>());

  // reset
  useEffect(() => {
    if (!modalOpen) {
      setAddedIds(prev => (!prev.size ? prev : new Set<string>()));
      setRemovedIds(prev => (!prev.size ? prev : new Set<string>()));
    }
  }, [modalOpen]);

  const onAdded = useCallback((id: string) => {
    setAddedIds(prev => add(prev, id));
    setRemovedIds(prev => remove(prev, id));
  }, []);

  const onRemoved = useCallback((id: string) => {
    setAddedIds(prev => remove(prev, id));
    setRemovedIds(prev => add(prev, id));
  }, []);

  const onUndoAdded = useCallback((id: string) => {
    setAddedIds(prev => remove(prev, id));
  }, []);

  return {
    addedIds,
    removedIds,
    onAdded,
    onUndoAdded,
    onRemoved,
  };
};
